// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import {
    CartModel,
    InvoiceModel,
    OrderModel,
    PaymentMethodDto,
    ShipToModel,
    StateModel,
} from "@insite/client-framework/Types/ApiModels";
import { XCheckoutStateType } from "@werner/App/Store/Reducers/xCheckout";
import breakpoints from "@werner/XDL/PreStyle/breakpoints";
import systemZIndex from "@werner/XDL/PreStyle/zIndex";
import isDate from "lodash/isDate";

/**
 * To load JS script with callback.
 * you can pass one url or multiple
 * Example:
 * getScripts.urls([ // url1, url2]).then(() => { //callback handler });
 */
export const getScripts = {
    url: (url: string) => {
        return new Promise((resolve, reject) => {
            if (typeof document !== "undefined") {
                const script = document.createElement("script");
                script.type = "text/javascript";
                script.src = url;
                script.addEventListener("load", () => resolve(script), false);
                script.addEventListener("error", () => reject(script), false);
                document.body.appendChild(script);
            }
        });
    },
    urls: (urls: string[]) => {
        return Promise.all(urls.map(getScripts.url));
    },
};

export const isSmallMobileScreen = () =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 480px)").matches;

export const isMobileScreen = () => typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;

export const isTabletScreen = () => typeof window !== "undefined" && window.matchMedia("(max-width: 1024px)").matches;

export const isWideScreen = () => typeof window !== "undefined" && window.matchMedia("(min-width: 2200px)").matches;

/**
 * To convert string to boolean
 * @param value
 */
export const parseBoolean = (value?: string) => (value ? value?.toLowerCase() === "true" : false);

/**
 * Method use to pass for props not accept undefined
 */
export const doNothing = () => {};

export const formatNumber = (number: number, decimalPlaces = 2) => {
    return (Math.round(number * 100) / 100).toFixed(decimalPlaces);
};

/**
 * Helper function to export data to csv file
 * @param fields
 * @param data
 * @param filename
 */
export const exportToCSV = async (fields: string[], data: (string | number | null)[][], filename?: string) => {
    const dataForUnparse = {
        fields,
        data,
    };

    const { unparse } = await import(/* webpackChunkName: "papaparse" */ "papaparse");
    const csv = unparse(dataForUnparse);
    const csvBlob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
    const fileName = `${filename || "csv_exported_file"}_${Math.random() * 10000}.csv`;
    const downloadLink = document.createElement("a");
    downloadLink.href = window.URL.createObjectURL(csvBlob);
    downloadLink.setAttribute("download", fileName);
    downloadLink.click();
};

export const getSystemZIndex = (key: string): number => {
    const zIndexValues = systemZIndex as any;
    return zIndexValues[key] || -1;
};

export const isNonEmptyString = (value: string | undefined) => value !== undefined && value.trim() !== "";

export const generateSlidesPerViewMap = (opts?: { [key: number]: number }) => {
    const { maxWidths } = breakpoints;
    const values: any = {};
    if (!opts) {
        values[maxWidths[0]] = {
            slidesPerView: 1,
        };
        values[maxWidths[1]] = {
            slidesPerView: 2,
        };
        values[maxWidths[2]] = {
            slidesPerView: 3,
        };
        values[maxWidths[3]] = {
            slidesPerView: 4,
        };
        return values;
    }

    Object.keys(opts).forEach(key => {
        if (!maxWidths[+key]) {
            return;
        }
        values[maxWidths[+key]] = {
            slidesPerView: +opts[+key],
        };
    });
    return values;
};

export const filterByPropertyForValue = (data: any, property: string, filterValue: any, single?: boolean) => {
    if (!data) {
        return;
    }
    let filteredData = [];
    if (filterValue instanceof Array) {
        filteredData = data?.filter((o: any) => filterValue.includes(o[property]));
    } else {
        filteredData = data.filter((o: any) => o[property] === filterValue);
    }
    if (single) {
        return filteredData[0];
    }

    return filteredData;
};

export const getYoutubeThumbnail = (url: string) => {
    // This regex matches all youtube urls ( embed, shortlinks, full-url )
    if (!url) {
        return "";
    }
    const match = url.match(
        /^((?:https?:)?\/\/)?((?:www|m)\.)?((?:youtube(-nocookie)?\.com|youtu.be))(\/(?:[\w\-]+\?v=|shorts\/|embed\/|v\/)?)([\w\-]+)(\S+)?$/,
    );

    if (!match?.length) {
        return "";
    }
    const videoId = match[6];
    return `https://img.youtube.com/vi/${videoId}/default.jpg`;
};

export const isSiteMessageEmpty = (message: string) => siteMessage(message) === message;

export const isPaymentMethodCreditCard = (cart?: CartModel, paymentMethod?: PaymentMethodDto) =>
    cart?.paymentOptions?.cardTypes?.find(c => c.value === cart.paymentMethod?.cardType) ||
    cart?.paymentOptions?.cardTypes?.find(c => c.value === paymentMethod?.cardType);

export const getPaymentMethodValue = (creditCardData: XCheckoutStateType["payment"]) => {
    const {
        paymentMethod,
        isSavedPayment,
        savedPaymentDescription,
        isCreditCard,
        cardHolderName,
        lastFour,
        expirationDate,
    } = creditCardData;

    if (isSavedPayment) {
        const splittedString = savedPaymentDescription?.split("(");
        return splittedString && splittedString?.length > 0
            ? `${splittedString[0]} <br/><i>(${splittedString[1]}</i>`
            : savedPaymentDescription;
    }
    if (isCreditCard) {
        return `${cardHolderName} ${translate("Ending In")} ${lastFour}<br/><i>(${translate(
            "Exp",
        )} ${expirationDate})</i>`;
    }
    return paymentMethod?.toLowerCase() === "po" ? translate("Purchase Order") : paymentMethod;
};

export const formatDate = (date: Date | null) => {
    if (!date) {
        return "";
    }

    let dt = date;
    if (!isDate(dt)) {
        dt = new Date(dt);
    }

    return getLocalizedDateTime({
        dateTime: dt,
        options: {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        },
    });
};

export const mapApiFieldsToUiFields = <T>(obj: any, apiToUiKeyPairs: { [key: string]: keyof T }) => {
    const newObject: T = {} as T;
    Object.keys(apiToUiKeyPairs).forEach(apiKey => {
        newObject[apiToUiKeyPairs[apiKey]] = obj[apiKey];
    });
    return newObject;
};

export const getOneTimeAddress = (model: OrderModel | InvoiceModel) => {
    const isOneTimeAddress = parseBoolean(model.properties.isOneTimeAddress);
    if (!isOneTimeAddress) {
        const { stAddress1, stAddress2, shipToCity, shipToState, shipToPostalCode } = model;
        return {
            address1: stAddress1,
            address2: stAddress2,
            city: shipToCity,
            state: {
                abbreviation: shipToState,
            } as StateModel,
            postalCode: shipToPostalCode,
        } as ShipToModel;
    }

    const { oneTimeAddress1, oneTimeAddress2, oneTimeCity, oneTimeState, oneTimeZipCode } = model.properties;
    const oneTimeAddress = {
        address1: oneTimeAddress1,
        address2: oneTimeAddress2,
        city: oneTimeCity,
        state: {
            abbreviation: oneTimeState,
        } as StateModel,
        postalCode: oneTimeZipCode,
    } as ShipToModel;
    return oneTimeAddress;
};

export const checkIfUrl = (text: string) =>
    !!(
        text &&
        new RegExp(
            /(?:(?:https?|ftp):\/\/|\b(?:[a-z\d]+\.))(?:(?:[^\s()<>]+|\((?:[^\s()<>]+|(?:\([^\s()<>]+\)))?\))+(?:\((?:[^\s()<>]+|(?:\(?:[^\s()<>]+\)))?\)|[^\s`!()\[\]{};:'".,<>?«»""'']))?/gi,
        ).test(text)
    );

export const triggerEsc = () => {
    const escEvent = new KeyboardEvent("keydown", {
        key: "Escape",
        keyCode: 27,
        code: "Escape",
        which: 27,
        bubbles: true,
        cancelable: true,
    });

    document.dispatchEvent(escEvent);
};
